import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowUpRight,
  Cpu, Wrench, SlidersHorizontal, ClipboardList, IndianRupee, ShieldCheck,
} from 'lucide-react';
import { useReveal } from '../hooks/useReveal';
import { useLanguage } from '../context/LanguageContext';
import SlideArrow from '../components/SlideArrow';
import {
  MopedIcon, BicycleIcon, CarProfileIcon, TukTukIcon, ForkliftIcon, DroneIcon,
  AGVIcon, RoboticArmIcon, ConveyorIcon, TreadmillIcon, CellTowerIcon, IndustrialPumpIcon
} from '../components/Icons';

/* ── Helpers ── */
const Eyebrow = ({ children, light = false, uppercase = true, weight = 'medium' }) => (
  <p className={`font-mono text-[14px] sm:text-[16px] lg:text-[20px] tracking-[0.05em] ${weight === 'medium' ? 'font-medium' : 'font-normal'} ${uppercase ? 'uppercase' : ''} ${light ? 'text-[#FEFEFE]' : 'text-[#D64545]'}`}>
    {children}
  </p>
);

const GridBackdrop = () => (
  <div
    className="absolute inset-0 pointer-events-none select-none"
    style={{
      backgroundImage: 'linear-gradient(to right,#D9D9D9 1px,transparent 1px),linear-gradient(to bottom,#D9D9D9 1px,transparent 1px)',
      backgroundSize: '100px 100px',
      opacity: 0.3
    }}
  />
);

const BlueprintImage = ({ pad = 'pt-2 px-3 pb-4', tags = [] }) => (
  <div className="w-full h-full bg-white rounded-xl border border-[#D2D2D2] overflow-hidden shadow-sm flex flex-col min-h-[130px] sm:min-h-[260px] lg:min-h-0">
    <div className="flex-1 flex items-center justify-center overflow-hidden min-h-[100px] sm:min-h-[200px]">
      <img src="/MotorExploded.png" alt="Motor exploded view" className={`w-full h-full object-contain mix-blend-multiply scale-[0.85] sm:scale-[1.6] sm:translate-y-6 ${pad}`} />
    </div>
    {tags.length > 0 && (
      <div className="flex flex-wrap gap-1.5 sm:gap-2 px-3 pb-3 pt-2 sm:px-4 sm:pb-4">
        {tags.map((tag) => (
          <span key={tag} className="inline-flex items-center justify-center font-sans font-medium text-[12px] sm:text-[14px] px-2 sm:px-4" style={{ color: '#0042B8', backgroundColor: '#F8F9FB', height: '28px', minWidth: '70px', borderRadius: '6px' }}>
            {tag}
          </span>
        ))}
      </div>
    )}
  </div>
);

/* ── Custom partner icons (from design SVGs) ── */
const PartnerGaugeIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M3 22.0001V19.1413C3 11.9563 8.76875 6.02508 15.9537 6.00008C17.6648 5.99399 19.3603 6.32576 20.9428 6.97634C22.5254 7.62693 23.964 8.58354 25.1761 9.7913C26.3881 10.9991 27.3498 12.4342 28.006 14.0145C28.6622 15.5947 29 17.289 29 19.0001V22.0001C29 22.2653 28.8946 22.5197 28.7071 22.7072C28.5196 22.8947 28.2652 23.0001 28 23.0001H4C3.73478 23.0001 3.48043 22.8947 3.29289 22.7072C3.10536 22.5197 3 22.2653 3 22.0001Z" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 6V10" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13 23L21 12" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M25 17H28.8475" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.17375 17H7" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PartnerJoystickIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M26 19H6C5.44772 19 5 19.4477 5 20V26C5 26.5523 5.44772 27 6 27H26C26.5523 27 27 26.5523 27 26V20C27 19.4477 26.5523 19 26 19Z" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 19V11" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 11C18.2091 11 20 9.20914 20 7C20 4.79086 18.2091 3 16 3C13.7909 3 12 4.79086 12 7C12 9.20914 13.7909 11 16 11Z" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M25 15H21" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PartnerBatteryIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M25 8H4C2.89543 8 2 8.89543 2 10V22C2 23.1046 2.89543 24 4 24H25C26.1046 24 27 23.1046 27 22V10C27 8.89543 26.1046 8 25 8Z" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M31 12V20" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14.5 20L16.5 16H12.5L14.5 12" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PartnerCirclesIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M10 14C12.2091 14 14 12.2091 14 10C14 7.79086 12.2091 6 10 6C7.79086 6 6 7.79086 6 10C6 12.2091 7.79086 14 10 14Z" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 14C24.2091 14 26 12.2091 26 10C26 7.79086 24.2091 6 22 6C19.7909 6 18 7.79086 18 10C18 12.2091 19.7909 14 22 14Z" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 26C12.2091 26 14 24.2091 14 22C14 19.7909 12.2091 18 10 18C7.79086 18 6 19.7909 6 22C6 24.2091 7.79086 26 10 26Z" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 18V26" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M26 22H18" stroke="#D64545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ── Static (non-translatable) data ── */
const PRODUCT_STATICS = [
  { id: 'ivcu', image: '/iVCU.png', imgScale: 'scale-[1.18]' },
  { id: 'tcu',  image: '/TCU.png',  imgScale: 'scale-[1.4] translate-x-[12px] sm:translate-x-[54px] sm:scale-[1.5] lg:translate-x-[40px]' },
  { id: 'mcu',  image: '/MCU.png',  imgScale: 'scale-[1.18]' },
];

const PARTNER_ICONS = [
  { id: 'sensors',     Icon: PartnerGaugeIcon    },
  { id: 'hmi',         Icon: PartnerJoystickIcon },
  { id: 'battery',     Icon: PartnerBatteryIcon  },
  { id: 'accessories', Icon: PartnerCirclesIcon  },
];

const SERVICE_ICONS = [
  { id: 'platform-service', Icon: Cpu               },
  { id: 'poc',              Icon: Wrench            },
  { id: 'calibration',      Icon: SlidersHorizontal },
  { id: 'teardown',         Icon: ClipboardList     },
  { id: 'va-ve',            Icon: IndianRupee       },
  { id: 'certification',    Icon: ShieldCheck       },
];

const FEATURE_ICONS = [
  { key: 'f0', iconSrc: '/icons/Intelligent motion.png' },
  { key: 'f1', iconSrc: '/icons/Innovative.png'         },
  { key: 'f2', iconSrc: '/icons/ModelBasedSystem.png'   },
  { key: 'f3', iconSrc: '/icons/Catalog.png'            },
  { key: 'f4', iconSrc: '/icons/Plugs.png'              },
  { key: 'f5', iconSrc: '/icons/DigitalTwin.png'        },
];

const STAKEHOLDER_ICON_PATHS = [
  '/icons/supplier.png',
  '/icons/OemPartner.png',
  '/icons/Builder.png',
  '/icons/Users.png',
];

const TRANSPORT_ICONS = [MopedIcon, BicycleIcon, CarProfileIcon, TukTukIcon, ForkliftIcon, DroneIcon];
const INDUSTRIAL_ICONS = [AGVIcon, RoboticArmIcon, ConveyorIcon, TreadmillIcon, CellTowerIcon, IndustrialPumpIcon];

const WORKFLOW_ICON_PATHS = [
  '/icons/CursorClick.png',
  '/icons/CubeFocus.png',
  '/icons/Code.png',
  '/icons/Target.png',
  '/icons/ShoppingCartSimple.png',
  '/icons/PuzzlePiece.png',
  '/icons/Certificate (1).png',
];

/* ── Page ── */
const EcosystemPage = () => {
  const location = useLocation();
  const { setRef, isVisible } = useReveal();
  const { t, language } = useLanguage();

  const [activeTab, setActiveTab] = useState('products');
  const [wordSlot, setWordSlot]   = useState({ type: 'stable', idx: 0 });
  const sectionAnchors  = useRef({});
  const isManualScroll  = useRef(false);
  const wordIdxRef      = useRef(0);
  const directionRef    = useRef('up');
  const exitCleanupRef  = useRef(null);

  /* ── Translated data ── */
  const rotatingWords = t('ecosystemPage.hero.rotatingWords');

  const TABS = [
    { id: 'products',     label: t('ecosystemPage.tabs.products')     },
    { id: 'services',     label: t('ecosystemPage.tabs.services')     },
    { id: 'applications', label: t('ecosystemPage.tabs.applications') },
    { id: 'platform',     label: t('ecosystemPage.tabs.platform')     },
  ];

  const coreProductsData = t('ecosystemPage.products.coreProducts');
  const coreProducts = PRODUCT_STATICS.map((p, i) => ({ ...p, ...coreProductsData[i] }));

  const partnerProductsData = t('ecosystemPage.products.partnerProducts');
  const partnerProducts = PARTNER_ICONS.map((p, i) => ({ ...p, ...partnerProductsData[i] }));

  const servicesData = t('ecosystemPage.services.items');
  const coreServices = SERVICE_ICONS.map((s, i) => ({ ...s, ...servicesData[i] }));

  const featuresData = t('ecosystemPage.platform.features');
  const platformFeatures = FEATURE_ICONS.map((f, i) => ({ ...f, ...featuresData[i] }));

  const stakeholdersData = t('ecosystemPage.platform.stakeholders');
  const platformStakeholders = STAKEHOLDER_ICON_PATHS.map((iconSrc, i) => ({ iconSrc, ...stakeholdersData[i] }));

  const transportNames = t('ecosystemPage.applications.transportApps');
  const transportationApps = TRANSPORT_ICONS.map((Icon, i) => ({ Icon, name: transportNames[i] }));

  const industrialNames = t('ecosystemPage.applications.industrialApps');
  const industrialApps = INDUSTRIAL_ICONS.map((Icon, i) => ({ Icon, name: industrialNames[i] }));

  const workflowSteps = t('ecosystemPage.platform.workflowSteps');
  const builderWorkflow = WORKFLOW_ICON_PATHS.map((iconSrc, i) => ({ iconSrc, title: workflowSteps[i] }));

  const devSteps = t('ecosystemPage.platform.devSteps');
  const devApproach = devSteps.map((title, i) => ({ step: String(i + 1), title }));

  /* ── Rotating word animation ── */
  useEffect(() => {
    const SHOW_NORMAL = 800;
    const SHOW_FAST   = 130;
    const ANIM_NORMAL = 350;
    const ANIM_FAST   = 100;
    let tid;
    let cancelled = false;

    const advance = (nextIdx, speed) => {
      const prevIdx = wordIdxRef.current;
      wordIdxRef.current = nextIdx;
      const animMs = speed === 'normal' ? ANIM_NORMAL : ANIM_FAST;

      setWordSlot({ type: 'transitioning', fromIdx: prevIdx, toIdx: nextIdx, speed, id: Date.now() });
      clearTimeout(exitCleanupRef.current);
      exitCleanupRef.current = setTimeout(() => {
        if (!cancelled) setWordSlot({ type: 'stable', idx: nextIdx });
      }, animMs + 30);
    };

    const schedule = () => {
      const dir   = directionRef.current;
      const delay = dir === 'up' ? SHOW_NORMAL : SHOW_FAST;

      tid = setTimeout(() => {
        if (cancelled) return;
        const curr = wordIdxRef.current;

        if (dir === 'up') {
          if (curr < 3) {
            advance(curr + 1, 'normal');
          } else {
            directionRef.current = 'down';
          }
        } else {
          if (curr > 0) {
            advance(curr - 1, 'fast');
          } else {
            directionRef.current = 'up';
          }
        }
        schedule();
      }, delay);
    };

    schedule();
    return () => {
      cancelled = true;
      clearTimeout(tid);
      clearTimeout(exitCleanupRef.current);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && TABS.some((tabItem) => tabItem.id === tab)) {
      setActiveTab(tab);
      setTimeout(() => scrollToSection(tab), 100);
    }
  }, [location.search]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (isManualScroll.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-section-id');
            if (id) setActiveTab(id);
          }
        });
      },
      { threshold: 0.2, rootMargin: '-120px 0px -40% 0px' }
    );
    Object.values(sectionAnchors.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const registerAnchor = (id) => (el) => { sectionAnchors.current[id] = el; };

  const scrollToSection = (id) => {
    const el = sectionAnchors.current[id];
    if (!el) return;
    isManualScroll.current = true;
    setActiveTab(id);
    const top = el.getBoundingClientRect().top + window.scrollY - 132;
    window.scrollTo({ top, behavior: 'smooth' });
    setTimeout(() => { isManualScroll.current = false; }, 800);
  };

  return (
    <div data-testid="ecosystem-page" className="pt-[88px] lg:pt-[64px] bg-[#EEEFF1] font-sans text-[#4A4A4A]">

      {/* ── Hero ── */}
      <section className="relative bg-[#D64545] py-12 lg:py-32 overflow-hidden text-white">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none select-none"
          style={{
            backgroundImage: 'linear-gradient(to right,rgba(255,255,255,0.07) 1px,transparent 1px),linear-gradient(to bottom,rgba(255,255,255,0.07) 1px,transparent 1px)',
            backgroundSize: '100px 100px',
          }}
        />
        <img
          src="/TakumiT.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none w-[75%] sm:w-[52%]"
          style={{ right: '-8%', top: '-25%', height: 'auto', opacity: 0.31, filter: 'brightness(0) invert(1)' }}
        />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div>
            <h1 className="font-display leading-[1.2] tracking-tight text-[#1E1E1E]" style={{ fontWeight: 700, fontSize: language === 'jp' ? 'clamp(1.6rem, 6.5vw, 76px)' : 'clamp(1.6rem, 9vw, 96px)' }}>
              {t('ecosystemPage.hero.line1')}<br />
              <span style={{ whiteSpace: 'nowrap' }}>
              {t('ecosystemPage.hero.line2')}{' '}
              <span
                className="font-display inline-block text-white top-1 sm:top-3"
                style={{ fontWeight: 900, overflow: 'hidden', height: '1.2em', verticalAlign: 'bottom', position: 'relative' }}
              >
                {wordSlot.type === 'transitioning' ? (
                  <span
                    key={String(wordSlot.id)}
                    style={{
                      display: 'block',
                      animationName: wordSlot.speed === 'fast' ? 'slot-down' : 'slot-up',
                      animationDuration: wordSlot.speed === 'fast' ? '110ms' : '350ms',
                      animationTimingFunction: 'ease-in-out',
                      animationFillMode: 'both',
                      willChange: 'transform',
                    }}
                  >
                    {wordSlot.speed === 'fast' ? (
                      <>
                        <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{rotatingWords[wordSlot.toIdx]}</span>
                        <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{rotatingWords[wordSlot.fromIdx]}</span>
                      </>
                    ) : (
                      <>
                        <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{rotatingWords[wordSlot.fromIdx]}</span>
                        <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{rotatingWords[wordSlot.toIdx]}</span>
                      </>
                    )}
                  </span>
                ) : (
                  <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{rotatingWords[wordSlot.idx]}</span>
                )}
              </span>
              </span>
            </h1>
            <p className="mt-6 text-[16px] sm:text-[22px] font-sans text-[#4A4A4A] max-w-xl leading-relaxed">
              {t('ecosystemPage.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* ── Sticky sub-nav ── */}
      <div
        className="sticky top-[88px] lg:top-[64px] z-40 no-scrollbar"
        style={{ backgroundColor: '#3E3739', transform: 'translateZ(0)' }}
      >
        <div className="flex w-full sm:min-w-max">
          {TABS.map((tab, i) => (
            <button
              key={tab.id}
              onClick={() => scrollToSection(tab.id)}
              className={`flex-1 text-center py-2.5 sm:py-4 lg:py-3 px-1 sm:px-2 font-sans font-medium text-[11px] sm:text-[16px] lg:text-[16px] text-[#ECECF2] leading-tight sm:whitespace-nowrap transition-colors duration-200 ${
                i < TABS.length - 1 ? 'border-r border-[#ECECF2]/20' : ''
              } ${
                activeTab === tab.id
                  ? 'bg-[#ECECF2]/10'
                  : 'hover:bg-[#ECECF2]/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Core Products ── */}
      <section
        ref={(el) => { registerAnchor('products')(el); setRef('coreProducts')(el); }}
        data-section-id="products"
        className="relative py-10 lg:py-24 bg-[#ECECF2] overflow-hidden border-b border-[#D2D2D2]"
      >
        <GridBackdrop />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`mb-6 lg:mb-12 reveal ${isVisible.coreProducts ? 'reveal-in' : ''}`}>
            <Eyebrow>{t('ecosystemPage.products.eyebrow')}</Eyebrow>
            <h2 className="mt-3 font-display font-bold text-[28px] sm:text-[36px] lg:text-[48px] leading-[1.1] text-[#2C5AA0]">{t('ecosystemPage.products.heading')}</h2>
            <p className="mt-4 font-sans font-medium text-[16px] text-black leading-relaxed max-w-lg">{t('ecosystemPage.products.description')}</p>
          </div>
          <div className={`grid grid-cols-3 lg:grid-cols-3 gap-1.5 lg:gap-6 reveal ${isVisible.coreProducts ? 'reveal-in' : ''}`}>
            {coreProducts.map((product, i) => (
              <div
                key={product.id}
                className="group relative rounded-lg sm:rounded-xl border border-[#D2D2D2] bg-[#E5E6ED] overflow-hidden hover:border-[#D64545] hover:shadow-xl transition-all duration-300 min-h-[170px] lg:min-h-[460px]"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Default state */}
                <div className="transition-opacity duration-300 group-hover:opacity-0 group-hover:invisible absolute inset-0 flex flex-col overflow-hidden">
                  <div className="px-1.5 pt-1.5 sm:px-7 sm:pt-7 lg:px-5 lg:pt-5">
                    <p className="hidden sm:block font-sans font-normal text-[16px] lg:text-[13px] text-[#4A4A4A] mb-2">{product.kicker}</p>
                    <h3 className="font-sans font-bold text-[10px] sm:text-[32px] lg:text-[22px] text-[#D64545] leading-tight mb-0.5 sm:mb-3">{product.title}</h3>
                  </div>
                  <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden sm:min-h-[280px] lg:min-h-[200px] px-1 sm:px-2">
                    <img src={product.image} alt={product.title} className={`w-full h-full object-contain mix-blend-multiply transition-transform duration-300 ${product.imgScale}`} />
                  </div>
                  <div className="shrink-0 grid grid-cols-2 gap-0.5 px-0.5 pb-1 pt-0.5 sm:flex sm:flex-wrap sm:gap-2 sm:px-7 sm:pb-7 sm:pt-3 lg:px-5 lg:pb-5 lg:pt-2 lg:gap-1.5">
                    {product.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-[#EEEFF1] px-0.5 py-0 sm:px-3 sm:py-1 font-sans font-normal text-[5px] sm:text-[12px] lg:text-[10px] text-[#D64545] leading-[1.4] text-center truncate">{tag}</span>
                    ))}
                  </div>
                </div>
                {/* Hover state */}
                <div className="card-bg-reveal px-2 py-2 sm:px-7 sm:py-8 lg:px-5 lg:py-5 bg-[#D64545] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 absolute inset-0 flex flex-col">
                  <h3 className="title-bounce font-sans font-bold text-[11px] sm:text-[40px] lg:text-[26px] text-[#EEEFF1] leading-tight">{product.title}</h3>
                  <div className="flex-1" />
                  <p className="font-sans font-medium text-[7px] sm:text-[16px] lg:text-[13px] text-[#EEEFF1] leading-tight sm:leading-relaxed lg:leading-relaxed mb-1 sm:mb-6 lg:mb-4 line-clamp-4 sm:line-clamp-none">{product.description}</p>
                  <Link
                    to={`/ecosystem?tab=products#${product.id}`}
                    className="arrow-trigger inline-flex items-center gap-1 sm:gap-2 font-sans font-medium text-[8px] sm:text-[20px] lg:text-[14px] text-[#EEEFF1] underline underline-offset-2 sm:underline-offset-4"
                  >
                    {t('ecosystemPage.products.viewDetails')} <SlideArrow Icon={ArrowUpRight} iconClass="h-2 w-2 sm:h-5 sm:w-5 lg:h-4 lg:w-4 text-[#EEEFF1]" strokeWidth={2} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Partner Products ── */}
      <section ref={setRef('partnerProducts')} className="py-10 lg:py-24 bg-white border-b border-[#D2D2D2]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header: right-aligned */}
          <div className={`grid lg:grid-cols-2 mb-6 lg:mb-12 reveal ${isVisible.partnerProducts ? 'reveal-in' : ''}`}>
            <div className="hidden lg:block" />
            <div className="pl-0 lg:pl-8">
              <Eyebrow uppercase={false}>{t('ecosystemPage.products.eyebrow')}</Eyebrow>
              <h2 className="mt-3 font-display font-bold text-[28px] sm:text-[36px] lg:text-[48px] leading-[1.1] text-[#2C5AA0]">{t('ecosystemPage.products.partnerTitle')}</h2>
              <p className="mt-4 font-sans font-medium text-[16px] text-black leading-relaxed max-w-lg">{t('ecosystemPage.products.partnerDescription')}</p>
            </div>
          </div>

          {/* Cards */}
          <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-4 reveal ${isVisible.partnerProducts ? 'reveal-in' : ''}`}>
            {partnerProducts.map((product, i) => (
              <div
                key={product.id}
                className="flex flex-row sm:flex-col gap-3 p-4 sm:p-6 border border-[#69717C] hover:shadow-lg transition-shadow duration-300 sm:min-h-[240px]"
                style={{
                  borderRadius: '13.93px',
                  backgroundColor: i % 2 === 0 ? '#F1F2F6' : '#E5E6ED',
                }}
              >
                <div className="flex-shrink-0 self-start pt-0.5">
                  <product.Icon />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <h3 className="font-sans font-bold text-[22px] sm:text-[32px] text-[#D64545] leading-tight sm:mt-4">{product.title}</h3>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2 sm:mt-auto sm:pt-6">
                    {product.items.map((item) => (
                      <span
                        key={item}
                        className="inline-flex items-center justify-center bg-white font-sans font-medium text-[#1E1E1E] px-2 sm:px-3"
                        style={{ height: '25.55px', fontSize: '12px', borderRadius: '4px' }}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ── Core Services ── */}
      <section
        ref={(el) => { registerAnchor('services')(el); setRef('coreServices')(el); }}
        data-section-id="services"
        className="relative py-10 lg:py-24 bg-[#E5E6ED] overflow-hidden border-b border-[#D2D2D2]"
      >
        <GridBackdrop />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`mb-6 lg:mb-12 reveal ${isVisible.coreServices ? 'reveal-in' : ''}`}>
            <Eyebrow uppercase={false}>{t('ecosystemPage.services.eyebrow')}</Eyebrow>
            <h2 className="mt-3 font-display font-bold text-[28px] sm:text-[36px] lg:text-[48px] leading-[1.1] text-[#2C5AA0]">{t('ecosystemPage.services.heading')}</h2>
            <p className="mt-4 font-sans font-medium text-[16px] text-[#4A4A4A] leading-relaxed max-w-lg">{t('ecosystemPage.services.description')}</p>
          </div>
          <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-6 reveal ${isVisible.coreServices ? 'reveal-in' : ''}`}>
            {coreServices.map((service) => (
              <div
                key={service.id}
                className="service-card bg-[#F1F2F6] p-4 sm:p-6 border border-[#4A4A4A] hover:shadow-lg transition-shadow duration-300 flex flex-col sm:min-h-[320px]"
                style={{ borderRadius: '8px' }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-lg bg-[#D64545] flex items-center justify-center text-white flex-shrink-0">
                      <service.Icon className="w-7 h-7" />
                    </div>
                    <h3 className="font-sans font-bold text-[24px] text-[#D64545] leading-tight">{service.title}</h3>
                  </div>
                  <p className="font-sans font-medium text-[16px] text-[#4A4A4A] leading-relaxed mb-5">{service.description}</p>
                </div>
                <div className="flex flex-wrap gap-2 mt-auto">
                  {service.features.map((f) => (
                    <span key={f} className="inline-flex items-center justify-center bg-white border border-[#D2D2D2] font-sans font-medium text-[12px] text-[#1E1E1E] px-3 whitespace-nowrap" style={{ height: '24.67px', borderRadius: '4px' }}>{f}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Domains & Applications ── */}
      <section
        ref={(el) => { registerAnchor('applications')(el); setRef('applications')(el); }}
        data-section-id="applications"
        className="relative py-10 lg:py-28 bg-[#F4F5F7] overflow-hidden border-b border-[#D2D2D2]"
      >
        <GridBackdrop />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">

          <div className={`mb-6 lg:mb-20 lg:max-w-[50%] reveal ${isVisible.applications ? 'reveal-in' : ''}`}>
            <Eyebrow uppercase={false} weight="normal">{t('ecosystemPage.applications.eyebrow')}</Eyebrow>
            <h2 className="mt-3 font-display font-bold text-[28px] sm:text-[36px] lg:text-[48px] leading-[1.1] text-[#2C5AA0]">
              {t('home.transport.heading')}
            </h2>
            <p className="mt-4 font-sans font-medium text-[16px] text-[#000000] max-w-xl leading-relaxed">
              {t('home.transport.description')}
            </p>
          </div>

          <div className="space-y-8 lg:space-y-24">

            {/* Transportation */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-12 items-stretch">
              <div className="lg:col-span-5 order-2 lg:order-1 flex flex-col">
                <BlueprintImage pad="pt-0 px-3 pb-0" tags={['Motors', 'Controllers', 'Telematics']} />
              </div>
              <div className="lg:col-span-7 order-1 lg:order-2 flex flex-col">
                <h3 className="font-sans font-bold text-[22px] sm:text-[28px] text-[#D64545] mb-3 sm:mb-6">
                  {t('ecosystemPage.applications.transportTitle')}
                </h3>
                <div className="grid grid-cols-3 gap-2 sm:gap-4 flex-1">
                  {transportationApps.map(({ Icon, name }) => (
                    <div
                      key={name}
                      className="flex flex-col items-center rounded-[16px] sm:rounded-[22px] bg-[#0042B8] p-2 sm:p-3 lg:p-4 aspect-square text-center text-white hover:bg-[#D64545] transition-colors duration-200 cursor-pointer"
                    >
                      <div className="flex-1 flex items-center justify-center w-full">
                        <Icon className="h-10 w-10 sm:h-11 sm:w-11 lg:h-14 lg:w-14 text-white" />
                      </div>
                      <span className="font-sans text-[10px] sm:text-[13px] lg:text-[17px] font-medium leading-[1.2]">{name}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex justify-end">
                  <Link to="/contact" className="arrow-trigger inline-flex items-center gap-1.5 text-[18px] font-medium text-[#0F0F0F] underline underline-offset-2 hover:text-[#D64545] transition-colors">
                    {t('ecosystemPage.applications.seeAll')} <SlideArrow Icon={ArrowUpRight} iconClass="h-5 w-5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Industrial */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-12 items-stretch">
              <div className="lg:col-span-7 flex flex-col">
                <h3 className="font-sans font-bold text-[22px] sm:text-[28px] text-[#D64545] mb-3 sm:mb-6">
                  {t('ecosystemPage.applications.industrialTitle')}
                </h3>
                <div className="grid grid-cols-3 gap-2 sm:gap-4 flex-1">
                  {industrialApps.map(({ Icon, name }) => (
                    <div
                      key={name}
                      className="flex flex-col items-center rounded-[16px] sm:rounded-[22px] bg-[#0042B8] p-2 sm:p-3 lg:p-4 aspect-square text-center text-white hover:bg-[#D64545] transition-colors duration-200 cursor-pointer"
                    >
                      <div className="flex-1 flex items-center justify-center w-full">
                        <Icon className="h-10 w-10 sm:h-11 sm:w-11 lg:h-14 lg:w-14 text-white" />
                      </div>
                      <span className="font-sans text-[10px] sm:text-[13px] lg:text-[17px] font-medium leading-[1.2]">{name}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex justify-end">
                  <Link to="/contact" className="arrow-trigger inline-flex items-center gap-1.5 text-[18px] font-medium text-[#0F0F0F] underline underline-offset-2 hover:text-[#D64545] transition-colors">
                    {t('ecosystemPage.applications.seeAll')} <SlideArrow Icon={ArrowUpRight} iconClass="h-5 w-5" />
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5 flex flex-col">
                <BlueprintImage pad="pt-0 px-3 pb-0" tags={['Motors', 'Controllers', 'Telematics', 'Sensors']} />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Platform Hero ── */}
      <section
        ref={registerAnchor('platform')}
        data-section-id="platform"
        className="relative text-white overflow-hidden flex items-center min-h-[260px] sm:min-h-[438px]"
        style={{ backgroundColor: '#D64545' }}
      >
        {/* GiphyLine SVG at all 4 corners */}
        <img src="/GiphyLine.gif" alt="" aria-hidden="true" loading="lazy" className="absolute pointer-events-none select-none top-0 left-0 w-auto" style={{ height: '50%' }} />
        <img src="/GiphyLine.gif" alt="" aria-hidden="true" loading="lazy" className="absolute pointer-events-none select-none top-0 right-0 w-auto" style={{ height: '50%' }} />
        <img src="/GiphyLine.gif" alt="" aria-hidden="true" loading="lazy" className="absolute pointer-events-none select-none bottom-0 left-0 w-auto" style={{ height: '50%' }} />
        <img src="/GiphyLine.gif" alt="" aria-hidden="true" loading="lazy" className="absolute pointer-events-none select-none bottom-0 right-0 w-auto" style={{ height: '50%' }} />

        <div className="relative w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-16">
          <div className="max-w-3xl">
            <Eyebrow light>{t('ecosystemPage.platform.eyebrow')}</Eyebrow>
            <h2 className="mt-4 font-display font-bold leading-[1.05] tracking-tight" style={{ whiteSpace: 'pre-line', color: '#FFFFFF', fontSize: 'clamp(2rem, 7vw, 64px)' }}>
              {t('ecosystemPage.platform.heading')}
            </h2>
            <p className="mt-6 font-sans font-medium text-[16px] leading-relaxed max-w-xl" style={{ color: '#FEFEFE' }}>
              {t('ecosystemPage.platform.description')}
            </p>
          </div>
        </div>
      </section>

      {/* ── Platform Capabilities ── */}
      <section ref={setRef('features')} className="pt-6 pb-8 lg:pt-12 lg:pb-24 bg-white border-b border-[#D2D2D2]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid lg:grid-cols-2 gap-8 mb-12 reveal ${isVisible.features ? 'reveal-in' : ''}`}>
            <div className="hidden lg:block" />
            <div className="pl-0 lg:pl-8">
              <Eyebrow uppercase={false}>{t('ecosystemPage.platform.whatYouGet.eyebrow')}</Eyebrow>
              <h2 className="mt-3 font-display font-bold text-[28px] sm:text-[36px] lg:text-[48px] leading-[1.1] text-[#2C5AA0]">{t('ecosystemPage.platform.whatYouGet.heading')}</h2>
              <p className="mt-4 font-sans font-medium text-[16px] text-[#000000] leading-relaxed max-w-md">
                {t('ecosystemPage.platform.whatYouGet.description')}
              </p>
            </div>
          </div>
          <div className={`grid gap-5 md:grid-cols-2 lg:grid-cols-3 reveal ${isVisible.features ? 'reveal-in' : ''}`}>
            {platformFeatures.map((feature, i) => (
              <div
                key={feature.key}
                className="bg-[#F1F2F6] p-5 border border-[#4A4A4A] hover:shadow-lg transition-shadow duration-300 flex flex-col"
                style={{ minHeight: '166px', borderRadius: '8px' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-16 h-16 rounded-lg bg-[#D64545] flex items-center justify-center flex-shrink-0">
                    <img src={feature.iconSrc} alt="" className="w-10 h-10 object-contain" />
                  </div>
                  <h3 className="font-sans font-bold text-[20px] text-[#D64545] leading-tight">{feature.title}</h3>
                </div>
                <p className="font-sans font-medium text-[14px] text-[#1E1E1E] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── End-to-End Integration ── */}
      <section ref={setRef('vision')} className="border-b border-[#D2D2D2] flex items-center sm:min-h-[478px]" style={{ backgroundColor: '#E5E6ED' }}>
        <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <div className={`mb-6 lg:mb-10 reveal ${isVisible.vision ? 'reveal-in' : ''}`}>
            <Eyebrow uppercase={false}>{t('ecosystemPage.platform.stakeholdersSection.eyebrow')}</Eyebrow>
            <h2 className="mt-3 font-display font-bold text-[28px] sm:text-[36px] lg:text-[48px] leading-[1.1] text-[#2C5AA0]">{t('ecosystemPage.platform.stakeholdersSection.heading')}</h2>
            <p className="mt-4 font-sans font-medium text-[16px] text-[#000000] leading-relaxed max-w-lg">
              {t('ecosystemPage.platform.stakeholdersSection.description')}
            </p>
          </div>
          <div className={`relative reveal ${isVisible.vision ? 'reveal-in' : ''}`}>
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 -translate-y-1/2 border-t-2 border-dashed border-[#D64545]" />
            <div className="relative grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {platformStakeholders.map((stakeholder) => (
                <div
                  key={stakeholder.label}
                  className="relative overflow-hidden bg-[#D64545] text-white flex flex-col justify-between px-5 py-5 w-full"
                  style={{ minHeight: '112px', borderRadius: '8px' }}
                >
                  {/* Background icon — bottom-right, fully decorative */}
                  <div className="absolute right-1 bottom-1 pointer-events-none">
                    <img src={stakeholder.iconSrc} alt="" className="w-20 h-20 object-contain opacity-70" />
                  </div>
                  <h3 className={`font-sans font-semibold leading-tight text-white pr-20 ${language === 'jp' ? 'text-[14px] lg:text-[15px]' : 'text-[15px] sm:text-[18px] lg:text-[22px]'}`}>{stakeholder.label}</h3>
                  <p className={`font-sans font-normal text-white leading-snug pr-20 mt-2 ${language === 'jp' ? 'text-[11px] lg:text-[12px]' : 'text-[12px] lg:text-[13px]'}`}>{stakeholder.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Development Approach ── */}
      <section ref={setRef('devApproach')} className="relative py-10 lg:py-24 bg-[#E5E6ED] overflow-hidden border-b border-[#D2D2D2]">
        <GridBackdrop />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid lg:grid-cols-2 gap-8 lg:gap-12 reveal ${isVisible.devApproach ? 'reveal-in' : ''}`}>

            {/* Left — approach steps */}
            <div className="lg:pt-8">
              <Eyebrow uppercase={false}>{t('ecosystemPage.platform.devApproachSection.eyebrow')}</Eyebrow>
              <h2 className="mt-3 font-display font-bold text-[28px] sm:text-[36px] lg:text-[48px] leading-[1.1] text-[#2C5AA0]">
                {t('ecosystemPage.platform.devHeading')}
              </h2>
              <p className="mt-4 font-sans font-medium text-[16px] text-[#000000] leading-relaxed">
                {t('ecosystemPage.platform.devApproachSection.description')}
              </p>
              <div className="mt-6 flex flex-col gap-3">
                {devApproach.map((item) => (
                  <div
                    key={item.step}
                    className="flex items-center gap-3 border border-[#B6B6B6] bg-white w-full py-2 sm:py-0"
                    style={{ minHeight: '64px', borderRadius: '4px' }}
                  >
                    <div
                      className="flex-shrink-0 flex items-center justify-center bg-[#D64545] ml-2 w-10 h-10 sm:w-14 sm:h-14"
                    >
                      <span className="font-sans font-bold text-[16px] sm:text-[20px]" style={{ color: '#F8F9FB' }}>{item.step}</span>
                    </div>
                    <span className="font-sans font-medium text-[15px] sm:text-[20px] text-[#1E1E1E] leading-snug">{item.title}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — builder workflow */}
            <div className="lg:pl-32 lg:-mt-6">
              <h3 className="font-sans font-bold text-[20px] sm:text-[32px] text-[#D64545] mb-3 sm:mb-8">{t('ecosystemPage.platform.workflowHeading')}</h3>
              <div className="flex flex-col">
                {builderWorkflow.map((step, i) => (
                  <div
                    key={step.title}
                    className={`relative flex items-center gap-3 sm:gap-6 ${i < builderWorkflow.length - 1 ? 'pb-2 sm:pb-8 lg:pb-12' : ''}`}
                  >
                    {i < builderWorkflow.length - 1 && (
                      <div
                        className="absolute left-[22px] top-[44px] sm:left-[36px] sm:top-[72px] border-l-2 border-dashed border-[#2C5AA0]"
                        style={{ height: 'calc(100% - 16px)' }}
                      />
                    )}
                    <div
                      className="relative flex-shrink-0 bg-white flex items-center justify-center border border-[#535353] w-[44px] h-[44px] sm:w-[72px] sm:h-[72px] rounded-[8px]"
                    >
                      <img src={step.iconSrc} alt="" className="w-5 h-5 sm:w-9 sm:h-9 object-contain" />
                    </div>
                    <span className="font-sans font-medium text-[13px] sm:text-[24px]" style={{ color: '#535353' }}>{step.title}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative overflow-hidden py-10 lg:py-0 sm:min-h-[425px]" style={{ backgroundColor: '#E5E6ED' }}>
        <img
          src="/Whiplash T line.png"
          alt=""
          aria-hidden="true"
          className="hidden sm:block absolute right-0 top-0 h-[160%] w-auto object-contain pointer-events-none select-none"
          style={{ filter: 'brightness(0) saturate(100%) invert(36%) sepia(94%) saturate(651%) hue-rotate(322deg) brightness(105%)' }}
        />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center sm:min-h-[425px]">
          <div className="max-w-5xl">
            <h2 className="font-display font-semibold leading-[1.1] tracking-tight" style={{ color: '#E71B2E', fontSize: 'clamp(2rem, 7vw, 64px)' }}>
              {t('ecosystemPage.cta.heading')}
            </h2>
            <p className="mt-6 font-sans font-medium text-[16px] sm:text-[24px] text-[#000000] leading-relaxed max-w-3xl">
              {t('ecosystemPage.cta.description')}
            </p>
            <div className="mt-6 lg:mt-10">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center border border-[#0F0F0F] px-8 py-3 text-[18px] font-semibold font-sans text-[#0F0F0F] hover:bg-[#D64545] hover:text-black hover:border-[#D64545] active:bg-[#0042B8] active:text-white active:border-[#0042B8] transition-all duration-300"
              >
                {t('ecosystemPage.cta.button')}
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default EcosystemPage;
